import React from 'react';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import type { Category, SavedQuery, UUID } from '@/types';
import { useDatasourceId } from '@/hooks';

export type GetSavedQueriesResult = {
  categories: Category[];
  savedQueries: SavedQuery[];
};

type TDependantQueriesInput = { datasourceId: UUID; seriesIds: UUID[] };

const isDependantQueriesInput = (
  input: unknown,
): input is TDependantQueriesInput =>
  typeof input === 'object' && input !== null && 'seriesIds' in input;

export const GET_SAVED_QUERIES = 'getSavedQueries';

export const apiSlice = createApi({
  reducerPath: 'savedQueriesApi',
  baseQuery: fetchBaseQuery({
    baseUrl: '/api/v1/datasources/',
    credentials: 'same-origin',
  }),
  endpoints: (builder) => ({
    [GET_SAVED_QUERIES]: builder.query<
      GetSavedQueriesResult,
      UUID | TDependantQueriesInput
    >({
      query: (input) =>
        isDependantQueriesInput(input)
          ? `${
              input.datasourceId
            }/saved-queries?seriesIds=${input.seriesIds.join(',')}`
          : `${input}/saved-queries`,
    }),
  }),
});

export const { useGetSavedQueriesQuery, usePrefetch } = apiSlice;

export const useGetSavedQueries = () => {
  const datasourceId = useDatasourceId();
  return useGetSavedQueriesQuery(datasourceId);
};

export const usePrefetchSavedQueries = () => {
  const datasourceId = useDatasourceId();
  const prefetch = usePrefetch(GET_SAVED_QUERIES);

  return React.useCallback(() => prefetch(datasourceId), [datasourceId]);
};
